import { type FC, useCallback, useMemo, useState } from 'react'

import { Trans, useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { Button } from 'components/Button/Button'
import SubscribeProfileModal from 'components/UI/Modal/profileModals/SubscribeProfileModal/SubscribeProfileModal'
import ModalWrapper from 'features/ModalWrapper/ModalWrapper'
import styles from 'features/NotificationCard/_NotificationCard.module.scss'
import { getLink } from 'helpers/appLinks'
import { toFixedNumber } from 'helpers/dataTransformations'
import { useSubscribeMutation } from 'helpers/hooks/subscription/useSubscribeMutation'
import { useSubscriptionPlans } from 'helpers/hooks/subscription/useSubscriptionPlans'

const SubscriptionExpiredNotifcation: FC<{ data: any }> = ({ data }) => {
  const { t } = useTranslation()

  const [disableBtn, setDisableBtn] = useState(false)
  const [subscribeModalOpen, setSubscribeModalOpen] = useState(false)

  const otherUserId = useMemo(
    () => data?.sender?.id ?? undefined,
    [data?.sender?.id]
  )

  const subscriptionPlans = useMemo(
    () => data?.subscription_plans ?? [],
    [data?.subscription_plans]
  )

  const promoCampaign = useMemo(
    () => data?.promo_campaign ?? null,
    [data?.promo_campaign]
  )

  const shouldLoadSubscriptionPlans =
    subscribeModalOpen &&
    !!otherUserId &&
    subscriptionPlans.length === 0

  const {
    selectedSubscriptionPlan,
    handleSetSelectedSubscriptionPlan,
    queryKey,
  } = useSubscriptionPlans({
    otherUserId,
    shouldLoadSubscriptionPlans,
  })

  const { subscribeMutation } = useSubscribeMutation({
    otherUserId,
    selectedSubscriptionPlan,
    t,
    onClose: () => setSubscribeModalOpen(false),
    invalidateKey: queryKey,
    onSubscribed: () => setDisableBtn(true),
  })

  const openSubscribe = useCallback(() => {
    if (disableBtn) return
    setSubscribeModalOpen(true)
  }, [disableBtn])

  const getBySubscriptionStatus = useCallback(
    (d: any) => {
      if (d?.subscription_status === 'expired' || d?.isToast) {
        return (
          <div className={styles.btnsRow}>
            <Button
              text={
                <p>
                  <Trans i18nKey='notifications:subscribeFor'>
                    subscribe
                    <span className={styles.contentText}>
                      {{ price: toFixedNumber(d?.subscription?.price) } as any}
                    </span>
                    mo
                  </Trans>
                </p>
              }
              customClass='notificationPriceButton'
              width='fit'
              color='blue'
              padding='1-15'
              disabled={disableBtn}
              clickFn={openSubscribe}
            />

            {subscribeModalOpen && (
              <ModalWrapper
                open={subscribeModalOpen}
                setOpen={setSubscribeModalOpen}
                customClass='subscribe-profile-modal-modal'
                hasCloseButton
              >
                <SubscribeProfileModal
                  displayName={d?.sender?.name}
                  avatar={d?.sender?.avatar}
                  username={d?.sender?.username}
                  id={otherUserId}
                  selected={selectedSubscriptionPlan}
                  setSelected={handleSetSelectedSubscriptionPlan}
                  initialPlans={subscriptionPlans}
                  initialCampaign={promoCampaign}
                  subscribeFn={(paymentMethod: {
                    method: 'card' | 'deposit'
                    cardId: string | undefined
                    rebill?: boolean
                    taxRate?: any
                    taxRateFee?: any
                  }) => {
                    subscribeMutation.mutate(paymentMethod)
                  }}
                  isLoading={subscribeMutation.isLoading}
                />
              </ModalWrapper>
            )}
          </div>
        )
      }

      return null
    },
    [
      disableBtn,
      openSubscribe,
      subscribeModalOpen,
      otherUserId,
      selectedSubscriptionPlan,
      handleSetSelectedSubscriptionPlan,
      subscriptionPlans,
      promoCampaign,
      subscribeMutation,
    ]
  )

  return (
    <>
      <Trans i18nKey='notifications:subscriptionExpired'>
        subscription
        <Link to={getLink.profile(data?.sender?.id)}>
          {{ name: data?.sender?.name } as any}
        </Link>
        expired
      </Trans>

      {getBySubscriptionStatus(data)}
    </>
  )
}

export default SubscriptionExpiredNotifcation
